import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import API from "../../Api/api";

const SERVER_BASE_URL = "https://dbskillsattendance.iilos.org";

const resolveImageUrl = (photoPath) => {
  if (!photoPath) return null;
  if (/^(https?:|data:|blob:)/i.test(photoPath)) return photoPath;
  return `${SERVER_BASE_URL}${photoPath.startsWith("/") ? photoPath : `/${photoPath}`}`;
};

const formatDate = (value) => {
  if (!value) return "";
  if (typeof value === "string" && value.length === 10 && value.includes("-")) {
    const [y, m, d] = value.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString("en-IN", {
      weekday: "short", month: "short", day: "numeric", year: "numeric",
    });
  }
  return new Date(value).toLocaleDateString("en-IN", {
    timeZone: "Asia/Kolkata", weekday: "short", month: "short", day: "numeric", year: "numeric",
  });
};

const formatTime = (value) =>
  value
    ? new Date(value).toLocaleTimeString("en-IN", {
        timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit", hour12: true,
      })
    : "--:--";

const statusColor = (status) => {
  if (status === "OnTime") return { bg: "#DBEAFE", text: "#1D4ED8" };
  if (status === "Late") return { bg: "#FEE2E2", text: "#DC2626" };
  if (status === "Early") return { bg: "#FEF3C7", text: "#D97706" };
  return { bg: "#EDEDED", text: "#555" };
};

// ─── Icons ──────────────────────────────────────────────────────────────
const Svg = ({ children, size = 18, color = "currentColor", fill = "none" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={fill} stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{children}</svg>
);
const IconBack = (p) => <Svg {...p}><polyline points="15 18 9 12 15 6" /></Svg>;
const IconRefresh = (p) => <Svg {...p}><polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" /></Svg>;
const IconClock = (p) => <Svg {...p}><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></Svg>;
const IconCamera = (p) => <Svg {...p}><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" /><circle cx="12" cy="13" r="4" /></Svg>;
const IconChevR = (p) => <Svg {...p}><polyline points="9 18 15 12 9 6" /></Svg>;
const IconClose = (p) => <Svg {...p}><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></Svg>;
const IconExpand = (p) => <Svg {...p}><polyline points="15 3 21 3 21 9" /><polyline points="9 21 3 21 3 15" /><line x1="21" y1="3" x2="14" y2="10" /><line x1="3" y1="21" x2="10" y2="14" /></Svg>;
const IconArrowIn = (p) => <Svg {...p}><circle cx="12" cy="12" r="10" /><polyline points="12 16 16 12 12 8" /><line x1="8" y1="12" x2="16" y2="12" /></Svg>;
const IconArrowOut = (p) => <Svg {...p}><circle cx="12" cy="12" r="10" /><polyline points="12 8 8 12 12 16" /><line x1="16" y1="12" x2="8" y2="12" /></Svg>;
const IconWhatsApp = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="#fff">
    <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.33 4.95L2.05 22l5.25-1.38a9.9 9.9 0 0 0 4.74 1.21h.01c5.46 0 9.91-4.45 9.91-9.91C21.96 6.45 17.5 2 12.04 2zm0 18.12h-.01a8.2 8.2 0 0 1-4.18-1.14l-.3-.18-3.12.82.83-3.04-.19-.31a8.2 8.2 0 0 1-1.26-4.36c0-4.54 3.69-8.23 8.24-8.23 2.2 0 4.27.86 5.82 2.42a8.17 8.17 0 0 1 2.41 5.82c0 4.54-3.7 8.2-8.24 8.2zm4.52-6.16c-.25-.12-1.46-.72-1.69-.8-.23-.08-.39-.12-.56.12-.17.25-.64.8-.78.96-.14.17-.29.19-.53.06-.25-.12-1.04-.38-1.98-1.22-.73-.65-1.23-1.46-1.37-1.7-.14-.25-.02-.38.11-.5.11-.11.25-.29.37-.43.12-.14.16-.25.25-.41.08-.17.04-.31-.02-.43-.06-.12-.56-1.34-.76-1.84-.2-.48-.41-.42-.56-.42-.14-.01-.31-.01-.47-.01-.17 0-.44.06-.67.31-.23.25-.87.85-.87 2.08s.89 2.41 1.01 2.58c.12.17 1.76 2.68 4.25 3.76.6.26 1.06.41 1.43.53.6.19 1.14.16 1.57.1.48-.07 1.46-.6 1.67-1.17.2-.58.2-1.07.14-1.17-.06-.11-.22-.17-.47-.29z" />
  </svg>
);

export default function Attendance() {
  const navigate = useNavigate();
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [userName, setUserName] = useState("User");
  const [sheetItem, setSheetItem] = useState(null);
  const [zoomed, setZoomed] = useState(null);
  const [sharingType, setSharingType] = useState(null);
  const fileCache = useRef({});

  const load = useCallback(async () => {
    try {
      const res = await API.get("/attendance/mine");
      if (res.data.success && Array.isArray(res.data.data)) {
        setAttendance([...res.data.data].sort((a, b) => new Date(b.date) - new Date(a.date)));
      }
    } catch (e) {
      console.log("Attendance error:", e?.response?.data || e.message);
    }
  }, []);

  useEffect(() => {
    (async () => {
      await load();
      setLoading(false);
    })();
    (async () => {
      try {
        const res = await API.get("/user/profile");
        const d = res.data && res.data.data;
        if (d) setUserName(d.username || d.name || "User");
      } catch (e) {
        console.log("Profile error:", e);
      }
    })();
  }, [load]);

  const refresh = async () => {
    setRefreshing(true);
    await load();
    setRefreshing(false);
  };

  const capitalizedName = userName ? userName.charAt(0).toUpperCase() + userName.slice(1) : "User";

  // Download the photos as soon as the sheet opens, so tapping WhatsApp can
  // share instantly (phones block the share sheet if we wait for a download
  // after the tap).
  useEffect(() => {
    if (!sheetItem) return;
    [sheetItem.checkIn?.photo, sheetItem.checkOut?.photo].forEach(async (path) => {
      const url = resolveImageUrl(path);
      if (!url || fileCache.current[url]) return;
      try {
        const blob = await (await fetch(url)).blob();
        fileCache.current[url] = new File([blob], "attendance.jpg", { type: blob.type || "image/jpeg" });
      } catch (e) {
        console.log("Photo prefetch failed:", e);
      }
    });
  }, [sheetItem]);

  const shareToWhatsApp = async (photoUrl, type) => {
    if (!photoUrl) return;
    const caption = `${type === "checkin" ? "Check In" : "Check Out"} by ${capitalizedName}`;
    const openWhatsAppText = () =>
      window.open(`https://wa.me/?text=${encodeURIComponent(caption)}`, "_blank");
    try {
      setSharingType(type);
      let file = fileCache.current[photoUrl];
      if (!file) {
        const blob = await (await fetch(photoUrl)).blob();
        file = new File([blob], "attendance.jpg", { type: blob.type || "image/jpeg" });
        fileCache.current[photoUrl] = file;
      }
      if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: caption, text: caption });
      } else {
        openWhatsAppText();
      }
    } catch (e) {
      if (e && e.name !== "AbortError") {
        console.log("Share error:", e);
        openWhatsAppText();
      }
    } finally {
      setSharingType(null);
    }
  };

  const sheetPhotos = sheetItem
    ? [
        {
          type: "checkin",
          title: "Check In",
          photo: resolveImageUrl(sheetItem.checkIn?.photo),
          time: formatTime(sheetItem.checkIn?.time),
          status: sheetItem.checkIn?.status || "Unknown",
        },
        {
          type: "checkout",
          title: "Check Out",
          photo: resolveImageUrl(sheetItem.checkOut?.photo),
          time: formatTime(sheetItem.checkOut?.time),
          status: sheetItem.checkOut?.status || "Unknown",
        },
      ]
    : [];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        .at-frame { min-height: 100dvh; background: #000; display: flex; justify-content: center; font-family: 'Plus Jakarta Sans', sans-serif; }
        .at-phone { width: 100%; max-width: 440px; min-height: 100dvh; background: #F4F6F9; position: relative; }
        @media (min-width: 480px) { .at-frame { padding: 20px; } .at-phone { min-height: calc(100dvh - 40px); border-radius: 30px; overflow: hidden; } }

        .at-header { background: linear-gradient(135deg, #0D1B2A, #1B2D45 55%, #163354); padding: 16px 18px 24px; display: flex; align-items: center; gap: 12px; color: #fff; }
        .at-icon-btn { width: 40px; height: 40px; border-radius: 20px; border: none; cursor: pointer; display: flex; align-items: center; justify-content: center; background: rgba(255,255,255,0.12); color: #fff; flex-shrink: 0; }
        .at-icon-btn.refresh { background: rgba(43,181,206,0.15); border: 1px solid rgba(43,181,206,0.3); color: #2BB5CE; }
        .at-title { font-size: 22px; font-weight: 800; letter-spacing: -0.5px; white-space: nowrap; }
        .at-sub { font-size: 12px; color: #93C5FD; font-weight: 500; margin-top: 3px; }
        @keyframes atSpin { to { transform: rotate(360deg); } }
        .spin { animation: atSpin 0.8s linear infinite; }

        .at-list { padding: 16px 16px 30px; }
        .at-card { background: #fff; border-radius: 20px; margin-bottom: 14px; overflow: hidden; box-shadow: 0 3px 10px rgba(15,23,42,0.06); }
        .at-date-row { display: flex; justify-content: space-between; align-items: center; padding: 14px 16px; border-bottom: 1px solid #F1F5F9; }
        .at-date-info { display: flex; align-items: center; gap: 10px; }
        .at-dot { width: 8px; height: 8px; border-radius: 4px; }
        .at-date { white-space: nowrap; font-size: 14px; font-weight: 700; color: #0F172A; }
        .at-hours { display: flex; align-items: center; gap: 4px; background: rgba(43,181,206,0.1); border: 1px solid rgba(43,181,206,0.2); border-radius: 16px; padding: 5px 10px; font-size: 12px; font-weight: 700; color: #2BB5CE; }
        .at-times { display: flex; padding: 14px 16px; }
        .at-time-col { flex: 1; }
        .at-time-head { display: flex; align-items: center; gap: 6px; margin-bottom: 6px; }
        .at-time-label { white-space: nowrap; font-size: 11px; font-weight: 700; color: #64748B; letter-spacing: 0.4px; text-transform: uppercase; }
        .at-time-value { font-size: 19px; font-weight: 800; color: #0F172A; letter-spacing: 0.5px; margin-bottom: 6px; }
        .at-badge { display: inline-block; padding: 4px 9px; border-radius: 14px; font-size: 11px; font-weight: 700; }
        .at-divider { width: 1px; background: #F1F5F9; margin: 4px 12px; }
        .at-view-btn { width: 100%; display: flex; justify-content: space-between; align-items: center; background: #F8FAFC; border: none; border-top: 1px solid #F1F5F9; padding: 12px 16px; cursor: pointer; font-family: inherit; }
        .at-view-btn span { display: flex; align-items: center; gap: 8px; font-size: 13px; font-weight: 700; color: #0F172A; }
        .at-accent { height: 3px; }

        .at-empty, .at-loading { text-align: center; padding: 60px 20px; color: #64748B; font-size: 14px; font-weight: 600; }
        .at-empty small { display: block; margin-top: 6px; color: #94A3B8; font-weight: 500; font-size: 13px; }

        .at-backdrop { position: fixed; inset: 0; background: rgba(15,23,42,0.65); z-index: 100; display: flex; align-items: flex-end; justify-content: center; font-family: 'Plus Jakarta Sans', sans-serif; }
        .at-sheet { width: 100%; max-width: 440px; max-height: 85vh; background: #fff; border-radius: 24px 24px 0 0; overflow: hidden; display: flex; flex-direction: column; animation: atUp 0.3s cubic-bezier(0.16,1,0.3,1); }
        @keyframes atUp { from { transform: translateY(60px); opacity: 0; } to { transform: none; opacity: 1; } }
        .at-handle { background: #0D1B2A; padding: 10px 0; display: flex; justify-content: center; }
        .at-handle i { width: 38px; height: 4px; border-radius: 2px; background: rgba(255,255,255,0.3); }
        .at-sheet-head { background: linear-gradient(90deg, #0D1B2A, #1B2D45); padding: 0 20px 16px; display: flex; align-items: center; color: #fff; }
        .at-sheet-title { font-size: 19px; font-weight: 800; letter-spacing: -0.3px; }
        .at-sheet-sub { font-size: 13px; color: #93C5FD; font-weight: 500; margin-top: 2px; }
        .at-sheet-close { margin-left: auto; width: 34px; height: 34px; border-radius: 17px; border: none; background: rgba(255,255,255,0.15); color: #fff; cursor: pointer; display: flex; align-items: center; justify-content: center; }
        .at-sheet-body { overflow-y: auto; padding: 18px 20px 34px; }
        .at-sec-head { display: flex; align-items: center; gap: 8px; margin-bottom: 12px; font-size: 13px; font-weight: 800; color: #475569; letter-spacing: 0.8px; }
        .at-sec-dot { width: 20px; height: 20px; border-radius: 10px; display: flex; align-items: center; justify-content: center; color: #fff; }
        .at-photo-card { position: relative; width: 100%; height: 200px; border-radius: 16px; overflow: hidden; background: #0F172A; border: 1px solid #E2E8F0; margin-bottom: 12px; cursor: pointer; padding: 0; }
        .at-photo-card img { width: 100%; height: 100%; object-fit: cover; display: block; }
        .at-expand { position: absolute; bottom: 10px; right: 10px; background: rgba(15,23,42,0.75); color: #fff; border-radius: 20px; padding: 5px 10px; display: flex; align-items: center; gap: 4px; font-size: 11px; font-weight: 700; }
        .at-no-photo { height: 140px; border-radius: 16px; background: #F8FAFC; border: 1.5px dashed #E2E8F0; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 6px; color: #475569; font-size: 14px; font-weight: 700; margin-bottom: 12px; }
        .at-meta { display: flex; justify-content: space-between; align-items: center; background: #F8FAFC; border: 1px solid #F1F5F9; border-radius: 12px; padding: 10px 14px; }
        .at-meta-time { display: flex; align-items: center; gap: 6px; font-size: 14px; font-weight: 700; color: #0F172A; }
        .at-wa-btn { width: 100%; margin-top: 10px; display: flex; align-items: center; justify-content: center; gap: 6px; background: #25D366; border: none; border-radius: 12px; padding: 12px; cursor: pointer; font-family: inherit; font-size: 13px; font-weight: 700; color: #fff; }
        .at-wa-btn:disabled { opacity: 0.6; cursor: not-allowed; }
        .at-sheet-divider { height: 1px; background: #F1F5F9; margin: 20px 0; }

        .at-zoom { position: fixed; inset: 0; background: rgba(10,15,26,0.96); z-index: 200; display: flex; flex-direction: column; font-family: 'Plus Jakarta Sans', sans-serif; }
        .at-zoom-head { display: flex; align-items: center; padding: 16px 20px; border-bottom: 1px solid rgba(255,255,255,0.1); color: #fff; }
        .at-zoom-title { font-size: 18px; font-weight: 800; }
        .at-zoom-sub { font-size: 13px; color: #93C5FD; font-weight: 500; margin-top: 2px; }
        .at-zoom-img { flex: 1; min-height: 0; padding: 16px; display: flex; align-items: center; justify-content: center; }
        .at-zoom-img img { max-width: 100%; max-height: 100%; border-radius: 12px; object-fit: contain; }
        .at-zoom-foot { padding: 16px 20px 28px; border-top: 1px solid rgba(255,255,255,0.1); }
        .at-zoom-foot .at-wa-btn { margin-top: 0; padding: 14px; border-radius: 14px; font-size: 14px; }
        .at-mini-spin { width: 16px; height: 16px; border: 2px solid rgba(255,255,255,0.4); border-top-color: #fff; border-radius: 50%; animation: atSpin 0.7s linear infinite; }
      `}</style>

      <div className="at-frame">
        <div className="at-phone">
          <div className="at-header">
            <button className="at-icon-btn" onClick={() => navigate("/dashboard")}><IconBack size={24} /></button>
            <div style={{ flex: 1 }}>
              <div className="at-title">My Attendance</div>
              <div className="at-sub">{attendance.length} records</div>
            </div>
            <button className="at-icon-btn refresh" onClick={refresh} disabled={refreshing}>
              <span className={refreshing ? "spin" : ""} style={{ display: "flex" }}><IconRefresh size={20} /></span>
            </button>
          </div>

          <div className="at-list">
            {loading ? (
              <div className="at-loading">Loading attendance records...</div>
            ) : attendance.length === 0 ? (
              <div className="at-empty">No attendance records found<small>Your check-in/check-out logs will appear here</small></div>
            ) : (
              attendance.map((item) => {
                const inStatus = item.checkIn?.status || "Unknown";
                const outStatus = item.checkOut?.status || "Unknown";
                const inColor = statusColor(inStatus);
                const outColor = statusColor(outStatus);
                const onTime = inStatus === "OnTime";
                return (
                  <div className="at-card" key={item._id}>
                    <div className="at-date-row">
                      <div className="at-date-info">
                        <span className="at-dot" style={{ background: onTime ? "#10B981" : "#F59E0B" }} />
                        <span className="at-date">{formatDate(item.date)}</span>
                      </div>
                      <div className="at-hours"><IconClock size={13} color="#2BB5CE" />{item.workingHours || "--"}</div>
                    </div>

                    <div className="at-times">
                      <div className="at-time-col">
                        <div className="at-time-head">
                          <span className="at-sec-dot" style={{ background: "#2BB5CE", width: 24, height: 24, borderRadius: 12 }}><IconArrowIn size={16} color="#fff" /></span>
                          <span className="at-time-label">Check In</span>
                        </div>
                        <div className="at-time-value">{formatTime(item.checkIn?.time)}</div>
                        <span className="at-badge" style={{ background: inColor.bg, color: inColor.text }}>{inStatus}</span>
                      </div>
                      <div className="at-divider" />
                      <div className="at-time-col">
                        <div className="at-time-head">
                          <span className="at-sec-dot" style={{ background: "#F0622D", width: 24, height: 24, borderRadius: 12 }}><IconArrowOut size={16} color="#fff" /></span>
                          <span className="at-time-label">Check Out</span>
                        </div>
                        <div className="at-time-value">{formatTime(item.checkOut?.time)}</div>
                        <span className="at-badge" style={{ background: outColor.bg, color: outColor.text }}>{outStatus}</span>
                      </div>
                    </div>

                    <button className="at-view-btn" onClick={() => setSheetItem(item)}>
                      <span><IconCamera size={16} color="#2BB5CE" />View Attendance Photos</span>
                      <IconChevR size={16} color="#94A3B8" />
                    </button>
                    <div className="at-accent" style={{ background: onTime ? "#2BB5CE" : "#F0622D" }} />
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* Photos bottom sheet */}
      {sheetItem && (
        <div className="at-backdrop" onClick={() => setSheetItem(null)}>
          <div className="at-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="at-handle"><i /></div>
            <div className="at-sheet-head">
              <div>
                <div className="at-sheet-title">Attendance Photos</div>
                <div className="at-sheet-sub">{formatDate(sheetItem.date)}</div>
              </div>
              <button className="at-sheet-close" onClick={() => setSheetItem(null)}><IconClose size={20} /></button>
            </div>
            <div className="at-sheet-body">
              {sheetPhotos.map((p, idx) => {
                const color = statusColor(p.status);
                const isIn = p.type === "checkin";
                return (
                  <div key={p.type}>
                    {idx > 0 && <div className="at-sheet-divider" />}
                    <div className="at-sec-head">
                      <span className="at-sec-dot" style={{ background: isIn ? "#2BB5CE" : "#F0622D" }}>
                        {isIn ? <IconArrowIn size={14} color="#fff" /> : <IconArrowOut size={14} color="#fff" />}
                      </span>
                      {p.title.toUpperCase()}
                    </div>
                    {p.photo ? (
                      <>
                        <button
                          className="at-photo-card"
                          onClick={() => setZoomed({
                            uri: p.photo,
                            type: p.type,
                            title: `${p.title} Photo`,
                            sub: `${formatDate(sheetItem.date)} • ${p.time}`,
                          })}
                        >
                          <img src={p.photo} alt={p.title} />
                          <span className="at-expand"><IconExpand size={14} color="#fff" />Tap to Zoom</span>
                        </button>
                        <div className="at-meta">
                          <span className="at-meta-time"><IconClock size={14} color="#64748B" />{p.time}</span>
                          <span className="at-badge" style={{ background: color.bg, color: color.text }}>{p.status}</span>
                        </div>
                        <button className="at-wa-btn" disabled={sharingType === p.type} onClick={() => shareToWhatsApp(p.photo, p.type)}>
                          {sharingType === p.type ? <div className="at-mini-spin" /> : <><IconWhatsApp />WhatsApp</>}
                        </button>
                      </>
                    ) : (
                      <div className="at-no-photo"><IconCamera size={32} color="#CBD5E1" />No {p.title} Photo Available</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Full-screen viewer */}
      {zoomed && (
        <div className="at-zoom">
          <div className="at-zoom-head">
            <div style={{ flex: 1 }}>
              <div className="at-zoom-title">{zoomed.title}</div>
              <div className="at-zoom-sub">{zoomed.sub}</div>
            </div>
            <button className="at-icon-btn" onClick={() => setZoomed(null)}><IconClose size={24} /></button>
          </div>
          <div className="at-zoom-img"><img src={zoomed.uri} alt={zoomed.title} /></div>
          <div className="at-zoom-foot">
            <button className="at-wa-btn" disabled={sharingType === zoomed.type} onClick={() => shareToWhatsApp(zoomed.uri, zoomed.type)}>
              {sharingType === zoomed.type ? <div className="at-mini-spin" /> : <><IconWhatsApp />Share on WhatsApp</>}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
